import { Accessibility } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { useA11yPrefs, type TextScale } from "@/state/useA11yPrefs";
import { cn } from "@/lib/utils";

const TEXT_SIZES: ReadonlyArray<{ value: TextScale; labelKey: string; sample: string }> = [
  { value: 100, labelKey: "textSize.normal", sample: "A" },
  { value: 125, labelKey: "textSize.large", sample: "A" },
  { value: 150, labelKey: "textSize.largest", sample: "A" }
];

/**
 * Native accessibility controls — a focus-trapped Dialog letting users set text
 * size, higher contrast, and reduced motion. Choices persist via `useA11yPrefs`
 * and apply in `A11yProvider`. Deliberately NOT a third-party overlay (the OKU
 * guide warns those break assistive tech); these are first-class app controls.
 *
 * Rendered in the Header so it's reachable on every page without colliding with
 * the sticky form save bars.
 */
export function AccessibilityControls() {
  const { t } = useTranslation("a11y");
  const { textScale, highContrast, reduceMotion, setTextScale, setHighContrast, setReduceMotion, reset } =
    useA11yPrefs();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={t("trigger")}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-foreground/80 transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Accessibility className="h-5 w-5" />
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>
            {t("description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Text size */}
          <fieldset>
            <legend className="text-sm font-medium">{t("textSize.legend")}</legend>
            <div role="radiogroup" aria-label={t("textSize.legend")} className="mt-2 grid grid-cols-3 gap-2">
              {TEXT_SIZES.map((opt) => {
                const active = textScale === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setTextScale(opt.value)}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded-md border px-3 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <span
                      aria-hidden
                      className="font-display font-bold leading-none"
                      style={{ fontSize: opt.value === 100 ? "1rem" : opt.value === 125 ? "1.25rem" : "1.5rem" }}
                    >
                      {opt.sample}
                    </span>
                    <span className="text-[11px] uppercase tracking-wide">{t(opt.labelKey)}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Higher contrast */}
          <label htmlFor="a11y-contrast" className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              id="a11y-contrast"
              checked={highContrast}
              onCheckedChange={(v) => setHighContrast(v === true)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium">{t("contrast.title")}</span>
              <span className="block text-xs text-muted-foreground">
                {t("contrast.description")}
              </span>
            </span>
          </label>

          {/* Reduced motion */}
          <label htmlFor="a11y-motion" className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              id="a11y-motion"
              checked={reduceMotion}
              onCheckedChange={(v) => setReduceMotion(v === true)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium">{t("motion.title")}</span>
              <span className="block text-xs text-muted-foreground">
                {t("motion.description")}
              </span>
            </span>
          </label>

          <div className="flex justify-end border-t pt-4">
            <Button type="button" variant="outline" size="sm" onClick={reset}>
              {t("reset")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
